import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { usePrograms, type Program } from './usePrograms';

export interface DealProgramLink {
  id: string;
  deal_id: string;
  program_id: string;
  attached_by: string;
  created_at: string;
  program: Program;
}

/** Programs attached to a single deal. Demo mode returns 1 mock link. */
export function useDealPrograms(dealId: string | undefined) {
  const { mode, dealershipId, userId } = useAuth();
  const { programs } = usePrograms();
  const [links, setLinks] = useState<DealProgramLink[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!dealId) { setLinks([]); setLoading(false); return; }
    if (mode === 'demo') {
      const p = programs[0];
      setLinks(p ? [{
        id: 'demo-link-1', deal_id: dealId, program_id: p.id,
        attached_by: userId ?? 'demo', created_at: new Date().toISOString(), program: p,
      }] : []);
      setLoading(false); return;
    }
    if (!dealershipId) return;
    setLoading(true);
    const { data } = await supabase
      .from('deal_programs')
      .select('id, deal_id, program_id, attached_by, created_at, programs(*)')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false });
    setLinks(((data ?? []) as any[]).map(r => ({
      id: r.id, deal_id: r.deal_id, program_id: r.program_id,
      attached_by: r.attached_by, created_at: r.created_at, program: r.programs as Program,
    })).filter(l => l.program));
    setLoading(false);
  }, [dealId, mode, dealershipId, programs, userId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { links, loading, refresh };
}

/** Count of deals linked to a given program (for ProgramDetail "Used in deals"). */
export function useProgramDealCount(programId: string | undefined) {
  const { mode, dealershipId } = useAuth();
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    if (!programId) { setCount(0); return; }
    if (mode === 'demo') { setCount(3); return; }
    if (!dealershipId) return;
    supabase
      .from('deal_programs')
      .select('id', { count: 'exact', head: true })
      .eq('program_id', programId)
      .then(({ count: c }) => setCount(c ?? 0));
  }, [programId, mode, dealershipId]);

  return count;
}